import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useMemo, useState } from "react";
import { Flex } from "antd";
import { useTranslation } from "react-i18next";
import { useAlert } from "@/shared/hooks";
import { AlertYellowIcon } from "@/shared/icons/AlertYellowIcon";
import { getErrorMessage } from "@/services/errors";
import { handleFullName } from "@/shared/utils";
import { appRouterPaths } from "@/services/navigation";
import { currentPersonSelector, setCurrentPerson } from "@/pages/ContactsPage/slice";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { AppButton, AppModal, AppText, InputSearch } from "@/shared/Components";
import { useDeletePersonMutation, useGetAllNotesQuery, type Person } from "@/services/api";
import { useModal } from "@/features/modals/lib";
import type { FC } from "react";
import type { MaybeType } from "@/shared/types";
import { ImportVcf } from "../ImportVcf";
import { AddContactButton } from "../AddContactButton";
import { getSwipedPersonName, isPersonSelected } from "../../helpers";
import { ContactsRow } from "../ContactsRow";
import styles from "./styles.module.scss";

type Props = {
  persons: Person[];
  setSwipedPerson: (personId: MaybeType<string>) => void;
  swipedPerson: MaybeType<string>;
};

export const ContactsMobileList: FC<Props> = ({ persons, setSwipedPerson, swipedPerson }) => {
  const { t } = useTranslation();
  const [filterQuery, setFilterQuery] = useState("");
  const dispatch = useAppDispatch();
  const currentPerson = useAppSelector(currentPersonSelector);
  const personSelected = isPersonSelected();
  const navigate = useNavigate();
  const paramsPersonId = useParams().personId;
  const { data: notes } = useGetAllNotesQuery();

  const {
    showModal: showDeleteModal,
    hideModal: hideDeleteModal,
    isModalOpen: isDeleteModalOpen,
  } = useModal();

  const {
    showModal: showHasNotesModal,
    hideModal: hideHasNotesModal,
    isModalOpen: isHasNotesModalOpen,
  } = useModal();

  const [deletePerson, { isLoading }] = useDeletePersonMutation();

  const { callError } = useAlert();

  const rowClickHandler = useCallback(
    (person: Person) => {
      if (personSelected && currentPerson?.id === person?.id) {
        dispatch(setCurrentPerson(null));
        setSwipedPerson(null);
        return;
      }

      if (paramsPersonId === person.id && !personSelected) {
        dispatch(setCurrentPerson(person));
        setSwipedPerson(null);
        return;
      }
      setSwipedPerson(null);
      navigate(
        `${appRouterPaths.contacts.base}/${location.pathname.split("/")[2] ?? "generalInfo"}/${person.id}`,
      );
      dispatch(setCurrentPerson(person));
      setFilterQuery("");
    },
    [personSelected, currentPerson?.id, paramsPersonId, dispatch, navigate],
  );

  const handleOpenModal = useCallback(
    (person: Person) => {
      const contactHasNotes = notes?.some((note) => note.personId === person?.id);
      if (contactHasNotes) {
        showHasNotesModal();
      } else {
        setSwipedPerson(person.id);
        showDeleteModal();
      }
    },
    [notes, showHasNotesModal, showDeleteModal, setSwipedPerson],
  );

  const filteredPersons = useMemo(() => {
    if (!filterQuery) return persons;
    return persons.filter((item) => {
      return (
        item.title?.includes(filterQuery) ||
        item.firstName?.includes(filterQuery) ||
        item.lastName?.includes(filterQuery) ||
        item.group?.includes(filterQuery)
      );
    });
  }, [persons, filterQuery]);

  const itemData = useMemo(
    () => ({
      persons: filteredPersons,
      handleOpenModal,
      rowClickHandler,
      setSwipedPerson,
      swipedPerson,
    }),
    [filteredPersons, handleOpenModal, rowClickHandler, setSwipedPerson, swipedPerson],
  );

  const deleteHandler = () => {
    if (!swipedPerson) return;
    deletePerson(swipedPerson)
      .unwrap()
      .then(() => {
        setSwipedPerson(null);
        hideDeleteModal();
      })
      .catch((e) => callError({ text: getErrorMessage(e.code) }));
  };

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.searchContainer}>
          <InputSearch
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            className={styles.search}
          />
        </div>
        <div className={styles["contacts-mobile-list"]}>
          <AutoSizer>
            {({ height, width }) => (
              <List
                width={width}
                itemSize={62}
                layout="vertical"
                height={height}
                itemCount={filteredPersons.length}
                itemData={itemData}
              >
                {ContactsRow}
              </List>
            )}
          </AutoSizer>
        </div>
        <div className={styles["add-button"]}>
          <AddContactButton type="float" />
        </div>
        <ImportVcf />
      </div>
      <AppModal
        centered
        maskClosable={false}
        closable={false}
        open={isDeleteModalOpen}
        footer={null}
        onCancel={() => {
          hideDeleteModal();
          setSwipedPerson(null);
        }}
        onOk={deleteHandler}
        width={360}
      >
        <Flex vertical gap={12} align="center" style={{ padding: "0 49px" }}>
          <AlertYellowIcon width={25} height={25} />
          <AppText
            size="md"
            weight="medium"
            style={{ textAlign: "center" }}
            className={styles["modal-text"]}
          >
            {t("contactsPage.deleteModal", {
              value: handleFullName(
                getSwipedPersonName(filteredPersons, swipedPerson).firstName,
                getSwipedPersonName(filteredPersons, swipedPerson).lastName,
              ),
            })}
          </AppText>
          <Flex justify="center" gap={12}>
            <AppButton onClick={hideDeleteModal}>{t("common.cancel")}</AppButton>
            <AppButton type="primary" onClick={deleteHandler} loading={isLoading}>
              {t("common.delete")}
            </AppButton>
          </Flex>
        </Flex>
      </AppModal>
      <AppModal
        centered
        maskClosable={false}
        closable={false}
        open={isHasNotesModalOpen}
        footer={null}
      >
        <Flex vertical gap={12} align="center" style={{ padding: "0 49px" }}>
          <AlertYellowIcon width={25} height={25} />
          <AppText
            size="sm"
            weight="medium"
            style={{ textAlign: "center" }}
            className={styles["modal-text"]}
          >
            {t("contactsPage.hasNotes")}
          </AppText>
          <Flex justify="center" gap={12}>
            <AppButton type="primary" onClick={hideHasNotesModal}>
              {t("common.gotIt")}
            </AppButton>
          </Flex>
        </Flex>
      </AppModal>
    </>
  );
};
